import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Briefcase,
    FileText,
    Presentation,
    Search,
    Lock,
    Eye,
    Shield,
    Sparkles,
    Calendar,
    HardDrive,
    ChevronRight,
    ArrowLeft,
    CheckCircle2,
    BookOpen,
    X,
    Filter,
    ArrowUpDown,
    Check
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { SecureDocumentViewer } from '../../../components/document/SecureDocumentViewer';
import { SkeletonCourseGrid } from '../../../components/common/SkeletonLoader';

interface JobSupportItem {
    id: number;
    title: string;
    description: string | null;
    category: string;
    file_type: string;
    file_size: number | null;
    created_at: string;
    updated_at: string;
}

export default function JobSupportView() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [hasAccess, setHasAccess] = useState<boolean | null>(null);
    const [materials, setMaterials] = useState<JobSupportItem[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedFormat, setSelectedFormat] = useState('All');
    const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'title' | 'size'>('newest');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeViewerDoc, setActiveViewerDoc] = useState<JobSupportItem | null>(null);

    useEffect(() => {
        const fetchJobSupport = async () => {
            const API_URL = (import.meta as any).env.VITE_API_BASE_URL || 'http://localhost:8000/api';
            const token = localStorage.getItem('token');

            try {
                const response = await fetch(`${API_URL}/student/job-support`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: 'application/json',
                    },
                });

                if (response.status === 403) {
                    setHasAccess(false);
                    setLoading(false);
                    return;
                }

                if (!response.ok) {
                    throw new Error('Failed to fetch job support data');
                }

                const data = await response.json();
                setHasAccess(data.has_access !== false);
                setMaterials(data.materials || []);
                setCategories(['All', ...(data.categories || [])]);
            } catch (err) {
                console.error('Job Support load error:', err);
                setHasAccess(false);
            } finally {
                setLoading(false);
            }
        };

        fetchJobSupport();
    }, []);

    const formatBytes = (bytes: number | null) => {
        if (!bytes || bytes === 0) return 'Document File';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getFormatBadge = (type: string) => {
        const t = (type || '').toLowerCase().replace('.', '');
        if (t === 'pdf') {
            return {
                label: 'PDF',
                colorClass: 'text-rose-500 bg-rose-500/10 border-rose-500/20',
                accentColor: '#ef4444',
                icon: FileText,
            };
        }
        if (t === 'docx' || t === 'doc') {
            return {
                label: 'DOCX',
                colorClass: 'text-sky-500 bg-sky-500/10 border-sky-500/20',
                accentColor: '#0284c7',
                icon: FileText,
            };
        }
        if (t === 'pptx' || t === 'ppt') {
            return {
                label: 'PPTX',
                colorClass: 'text-amber-500 bg-amber-500/10 border-amber-500/20',
                accentColor: '#f59e0b',
                icon: Presentation,
            };
        }
        return {
            label: t.toUpperCase(),
            colorClass: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20',
            accentColor: '#10b981',
            icon: FileText,
        };
    };

    const filteredMaterials = useMemo(() => {
        let list = materials.filter(item => {
            const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
            const matchesFormat =
                selectedFormat === 'All' ||
                (selectedFormat === 'pdf' && item.file_type.includes('pdf')) ||
                (selectedFormat === 'docx' && (item.file_type.includes('doc') || item.file_type.includes('docx'))) ||
                (selectedFormat === 'pptx' && (item.file_type.includes('ppt') || item.file_type.includes('pptx')));

            const matchesSearch =
                item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (item.category && item.category.toLowerCase().includes(searchQuery.toLowerCase()));

            return matchesCategory && matchesFormat && matchesSearch;
        });

        list.sort((a, b) => {
            if (sortBy === 'newest') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            if (sortBy === 'oldest') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            if (sortBy === 'title') return a.title.localeCompare(b.title);
            if (sortBy === 'size') return (b.file_size || 0) - (a.file_size || 0);
            return 0;
        });

        return list;
    }, [materials, selectedCategory, selectedFormat, searchQuery, sortBy]);

    if (loading) {
        return (
            <div className="space-y-6">
                <SkeletonCourseGrid />
            </div>
        );
    }

    // 1. ACCESS RESTRICTED SCREEN (Student has NOT been assigned Job Support)
    if (hasAccess === false) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center p-4">
                <div className="w-full max-w-lg bg-white dark:bg-brand-charcoal rounded-3xl p-8 sm:p-10 border-2 border-brand-border shadow-xl text-center space-y-6 animate-in zoom-in-95 duration-300">
                    <div className="w-20 h-20 mx-auto rounded-3xl bg-amber-500/10 text-amber-500 border border-amber-500/20 flex items-center justify-center">
                        <Lock size={36} />
                    </div>

                    <div className="space-y-2">
                        <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                            Restricted Portal Feature
                        </span>
                        <h2 className="text-2xl font-black text-brand-charcoal dark:text-white tracking-tight">
                            Job Support Access Locked
                        </h2>
                        <p className="text-sm text-brand-muted leading-relaxed max-w-md mx-auto">
                            The Job Support materials and document library are exclusively available to students who have been assigned access by the instructor or admissions team.
                        </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-brand-beige/50 dark:bg-slate-900/50 border border-brand-border text-left space-y-2.5">
                        <div className="flex items-start gap-2.5 text-xs text-brand-charcoal dark:text-gray-300 font-semibold">
                            <CheckCircle2 size={16} className="text-brand-emerald shrink-0 mt-0.5" />
                            <span>Confidential client SOPs, templates, and interview prep documents</span>
                        </div>
                        <div className="flex items-start gap-2.5 text-xs text-brand-charcoal dark:text-gray-300 font-semibold">
                            <CheckCircle2 size={16} className="text-brand-emerald shrink-0 mt-0.5" />
                            <span>Protected in-browser viewing with personalized student watermarking</span>
                        </div>
                    </div>

                    <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/student/dashboard')}
                            className="w-full sm:w-auto px-6 py-3.5 bg-brand-emerald hover:bg-emerald-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition shadow-lg flex items-center justify-center gap-2 cursor-pointer"
                        >
                            <ArrowLeft size={16} /> Return to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 2. ASSIGNED JOB SUPPORT LIBRARY (Unlocked)
    return (
        <div className="space-y-8 pb-12 animate-in fade-in duration-300">
            {/* Header Banner */}
            <div className="bg-gradient-to-br from-brand-charcoal via-slate-900 to-brand-charcoal text-white rounded-3xl p-6 sm:p-10 border border-slate-800 shadow-xl relative overflow-hidden">
                {/* Background Glow */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-brand-emerald/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10 space-y-4 max-w-3xl">
                    <div className="flex items-center gap-2.5 flex-wrap">
                        <span className="px-3.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-brand-emerald text-white shadow-lg shadow-brand-emerald/20 flex items-center gap-1.5">
                            <Sparkles size={13} /> Official Student Portal
                        </span>
                        <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-slate-800 text-emerald-400 border border-slate-700 flex items-center gap-1.5">
                            <Shield size={13} /> Protected View Only
                        </span>
                    </div>

                    <h1 className="text-2xl sm:text-4xl font-black text-white tracking-tight uppercase">
                        Job Support & Career Materials
                    </h1>

                    <p className="text-sm sm:text-base text-slate-300 font-medium leading-relaxed">
                        Access exclusive project documentation, client standard operating procedures, templates, and presentation guides assigned to your training track.
                    </p>

                    <div className="pt-2 flex items-center gap-6 text-xs text-slate-400 font-semibold flex-wrap">
                        <div className="flex items-center gap-2">
                            <BookOpen size={16} className="text-brand-emerald" />
                            <span>{materials.length} Documents Available</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Shield size={16} className="text-emerald-400" />
                            <span>In-App Zero-Download Security</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Standard LMS Search & Filter Belt for Students */}
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
                {/* Search Box */}
                <div className="relative flex-1 min-w-[260px] h-12 bg-white dark:bg-brand-charcoal border border-brand-border rounded-2xl flex items-center px-4 gap-2.5 shadow-sm focus-within:border-brand-emerald">
                    <Search size={18} className="text-brand-muted shrink-0 pointer-events-none" />
                    <input
                        type="text"
                        placeholder="Search documents by title or topic..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full bg-transparent border-none outline-none text-sm font-semibold text-brand-charcoal dark:text-white placeholder:text-brand-muted"
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            className="text-brand-muted hover:text-brand-charcoal dark:hover:text-white cursor-pointer"
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>

                {/* Dropdowns */}
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Format Filter */}
                    <div className="h-12 bg-white dark:bg-brand-charcoal border border-brand-border rounded-2xl flex items-center px-4 gap-2 shadow-sm">
                        <Filter size={15} className="text-brand-muted shrink-0" />
                        <span className="text-xs font-bold text-brand-muted whitespace-nowrap">Format:</span>
                        <select
                            value={selectedFormat}
                            onChange={e => setSelectedFormat(e.target.value)}
                            className="bg-transparent border-none outline-none text-xs font-bold text-brand-charcoal dark:text-white cursor-pointer"
                        >
                            <option value="All" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white">All Formats</option>
                            <option value="pdf" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white">PDF</option>
                            <option value="docx" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white">Word (.docx)</option>
                            <option value="pptx" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white">PowerPoint (.pptx)</option>
                        </select>
                    </div>

                    {/* Sort Dropdown */}
                    <div className="h-12 bg-white dark:bg-brand-charcoal border border-brand-border rounded-2xl flex items-center px-4 gap-2 shadow-sm">
                        <ArrowUpDown size={15} className="text-brand-muted shrink-0" />
                        <span className="text-xs font-bold text-brand-muted whitespace-nowrap">Sort:</span>
                        <select
                            value={sortBy}
                            onChange={e => setSortBy(e.target.value as any)}
                            className="bg-transparent border-none outline-none text-xs font-bold text-brand-charcoal dark:text-white cursor-pointer"
                        >
                            <option value="newest" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white">Newest First</option>
                            <option value="oldest" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white">Oldest First</option>
                            <option value="title" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white">Title (A - Z)</option>
                            <option value="size" className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white">File Size</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Category Filter Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
                {categories.map(cat => (
                    <button
                        key={cat}
                        type="button"
                        onClick={() => setSelectedCategory(cat)}
                        className={`px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-wider transition whitespace-nowrap cursor-pointer ${
                            selectedCategory === cat
                                ? 'bg-brand-emerald text-white shadow-lg shadow-brand-emerald/20'
                                : 'bg-white dark:bg-brand-charcoal text-brand-muted hover:text-brand-charcoal dark:hover:text-white border border-brand-border'
                        }`}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* Document Grid */}
            {filteredMaterials.length === 0 ? (
                <div className="bg-white dark:bg-brand-charcoal rounded-3xl p-12 text-center border-2 border-dashed border-brand-border space-y-4">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-brand-beige/50 dark:bg-slate-900 text-brand-muted flex items-center justify-center">
                        <Briefcase size={28} />
                    </div>
                    <div className="space-y-1">
                        <h3 className="text-lg font-black text-brand-charcoal dark:text-white">No documents found</h3>
                        <p className="text-xs text-brand-muted">
                            {searchQuery ? 'Try adjusting your search query or category filter.' : 'Your instructor has not uploaded any documents for this section yet.'}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredMaterials.map(item => {
                        const badge = getFormatBadge(item.file_type);
                        const FormatIcon = badge.icon;

                        return (
                            <div
                                key={item.id}
                                className="group bg-white dark:bg-brand-charcoal rounded-3xl p-6 border-2 border-brand-border hover:border-brand-emerald/40 transition-all duration-300 shadow-sm hover:shadow-xl flex flex-col justify-between space-y-6"
                            >
                                <div className="space-y-4">
                                    {/* Top Metadata */}
                                    <div className="flex items-start justify-between gap-3">
                                        <div
                                            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-105 transition"
                                            style={{
                                                backgroundColor: `${badge.accentColor}15`,
                                                borderColor: `${badge.accentColor}30`,
                                                color: badge.accentColor,
                                            }}
                                        >
                                            <FormatIcon size={24} />
                                        </div>

                                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${badge.colorClass}`}>
                                                {badge.label}
                                            </span>
                                            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-brand-beige/60 dark:bg-slate-800 text-brand-muted">
                                                {item.category || 'General'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Title & Description */}
                                    <div className="space-y-1.5">
                                        <h3 className="text-base font-black text-brand-charcoal dark:text-white group-hover:text-brand-emerald transition line-clamp-2">
                                            {item.title}
                                        </h3>
                                        {item.description && (
                                            <p className="text-xs text-brand-muted leading-relaxed line-clamp-3">
                                                {item.description}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Bottom Info & Action */}
                                <div className="space-y-4 pt-4 border-t border-brand-border/60">
                                    <div className="flex items-center justify-between text-[11px] text-brand-muted font-semibold">
                                        <span className="flex items-center gap-1">
                                            <HardDrive size={13} /> {formatBytes(item.file_size)}
                                        </span>
                                        {item.created_at && (
                                            <span className="flex items-center gap-1">
                                                <Calendar size={13} /> {formatDate(item.created_at)}
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => setActiveViewerDoc(item)}
                                        className="w-full py-3.5 px-4 rounded-2xl bg-brand-charcoal dark:bg-brand-emerald text-white hover:bg-brand-emerald dark:hover:bg-emerald-600 font-black text-xs uppercase tracking-widest transition flex items-center justify-center gap-2 shadow-md cursor-pointer group-hover:shadow-brand-emerald/20"
                                    >
                                        <Eye size={16} /> Open Document View <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Fullscreen Secure Document Viewer Overlay */}
            {activeViewerDoc && (
                <SecureDocumentViewer
                    documentId={activeViewerDoc.id}
                    title={activeViewerDoc.title}
                    fileType={activeViewerDoc.file_type}
                    studentName={user?.name}
                    studentEmail={user?.email}
                    onClose={() => setActiveViewerDoc(null)}
                />
            )}
        </div>
    );
}
